import httpService from './httpService';
import {
  BalanceSheet,
  BalanceStats,
  BankAccount,
  BankMutation,
  BankTransactionWithParticipation,
  ContributionPlanModel,
  ContributionPlanWithMembersModel,
  ContributionPlanWithPayments,
  CreateBankAccountModel,
  CreateBankTransactionModel,
  CreateContributionPaymentDto,
  CreateContributionPlanDto,
  GoCardlessAccount,
  GoCardlessInstitution,
  GoCardlessLink,
  InternalBankTransfer,
  ProfitAndLoss,
} from '../types/bankAccount';

export async function createBankAccount(bankAccount: CreateBankAccountModel): Promise<BankAccount> {
  const { data } = await httpService.post<BankAccount>(
    `/business/financial/bank-account`,
    bankAccount,
  );

  return data;
}

export async function deleteBankAccount(accountId: number): Promise<void> {
  await httpService.delete<BankAccount>(`/business/financial/bank-account/${accountId}`);
}

export async function getAllBankAccounts(): Promise<BankAccount[]> {
  const { data } = await httpService.get<BankAccount[]>(`/business/financial/bank-account/`);
  return data;
}

export async function getAllMutationsForBank(accountId: number): Promise<BankMutation[]> {
  return httpService
    .get<BankMutation[]>(`/business/financial/bank-account/${accountId}/mutations`)
    .then((res) => res.data);
}

export async function createTransactionWithParticipations(
  transactionData: CreateBankTransactionModel,
): Promise<BankTransactionWithParticipation> {
  return httpService
    .post(`/business/financial/transactions`, transactionData)
    .then((res) => res.data);
}

export async function createTransactionsWithParticipations(
  transactionData: CreateBankTransactionModel[],
): Promise<number[]> {
  return httpService
    .post(`/business/financial/transactions`, transactionData)
    .then((res) => res.data);
}

export async function updateBankTransaction(
  transactionId: number,
  transactionData: CreateBankTransactionModel,
): Promise<BankTransactionWithParticipation> {
  return httpService
    .put<BankTransactionWithParticipation>(
      `/business/financial/transactions/${transactionId}`,
      transactionData,
    )
    .then((res) => res.data);
}

export async function getDutchBanks(): Promise<GoCardlessInstitution[]> {
  return httpService
    .get<GoCardlessInstitution[]>('/business/financial/go-cardless/institutions')
    .then((res) => res.data);
}

export async function createGoCardlessLink(
  accountId: number,
  institutionId: string,
  lang: 'en' | 'nl',
  maxHistoricalDaysPreference?: number,
): Promise<GoCardlessLink> {
  return httpService
    .post<GoCardlessLink>(`/business/financial/bank-account/${accountId}/go-cardless/links`, {
      institutionId,
      lang,
      maxHistoricalDaysPreference,
    })
    .then((res) => res.data);
}

export async function getAccountsByRequisition(requisitionId: string) {
  return httpService
    .get<GoCardlessAccount[]>(`/business/financial/requisition/${requisitionId}/accounts`)
    .then((res) => res.data);
}

export async function createGoCardlessTransactions(
  goCardlessAccountId: string,
  veraAccountId: number,
) {
  return httpService
    .post(
      `/business/financial/bank-account/${veraAccountId}/go-cardless/transactions/sync-initial`,
      {
        goCardlessAccountId,
      },
    )
    .then((res) => res.data);
}

export async function fetchLatestGoCardlessTransactions(accountId: number) {
  return httpService
    .post(`/business/financial/bank-account/${accountId}/go-cardless/transactions/sync-latest`)
    .then((res) => res.data);
}

export async function unlinkAccountFromGoCardless(accountId: number) {
  return httpService.delete(`/business/financial/bank-account/${accountId}/requisition`);
}

export async function getAllTransactions(): Promise<BankTransactionWithParticipation[]> {
  return httpService
    .get<BankTransactionWithParticipation[]>(`/business/financial/transactions`)
    .then((res) => {
      console.log('Fetched transactions:', res.data);
      return res.data;
    });
}

export async function deleteTransaction(transactionId: number): Promise<void> {
  await httpService.delete(`/business/financial/transactions/${transactionId}`);
}

export async function createContributionPlan(
  plan: CreateContributionPlanDto,
): Promise<ContributionPlanModel> {
  return httpService
    .post<ContributionPlanModel>(`/business/financial/contribution-plans`, plan)
    .then((res) => res.data);
}

export async function getAllContributionPlans(): Promise<ContributionPlanWithMembersModel[]> {
  return httpService
    .get<ContributionPlanWithMembersModel[]>(`/business/financial/contribution-plans`)
    .then((res) => res.data);
}

export async function createContributionPayment(payment: CreateContributionPaymentDto) {
  return httpService
    .post(`/business/financial/contribution-plans/${payment.contributionPlanId}/payments`, payment)
    .then((res) => res.data);
}

export async function getContributionPlanWithPayments(
  contributionPlanId: number,
): Promise<ContributionPlanWithPayments> {
  return httpService
    .get<ContributionPlanWithPayments>(
      `/business/financial/contribution-plans/${contributionPlanId}`,
    )
    .then((res) => res.data);
}

export async function getContributionBalancesForConnections(): Promise<
  Record<number, BalanceStats>
> {
  return httpService
    .get<Record<number, BalanceStats>>(`/business/financial/contribution-plans/balances`)
    .then((res) => res.data);
}

export async function deleteContributionPlan(contributionPlanId: number): Promise<void> {
  return httpService.delete(`/business/financial/contribution-plans/${contributionPlanId}`);
}

export async function getBalanceStatement(): Promise<BalanceSheet> {
  return httpService.get(`/business/financial/balance-sheet`).then((res) => res.data);
}

export async function getProfitAndLoss(): Promise<ProfitAndLoss> {
  return httpService.get(`/business/financial/profit-and-loss`).then((res) => res.data);
}

// Internal bank transfers
export async function listInternalTransfers(): Promise<InternalBankTransfer[]> {
  return httpService
    .get<InternalBankTransfer[]>(`/business/financial/internal-transfers`)
    .then((res) => res.data);
}

export async function createInternalTransfer(body: {
  fromTransactionId?: number;
  toTransactionId?: number;
}): Promise<InternalBankTransfer> {
  return httpService
    .post<InternalBankTransfer>(`/business/financial/internal-transfers`, body)
    .then((res) => res.data);
}

export async function updateInternalTransfer(
  id: number,
  body: { fromTransactionId?: number; toTransactionId?: number },
): Promise<InternalBankTransfer> {
  return httpService
    .patch<InternalBankTransfer>(`/business/financial/internal-transfers/${id}`, body)
    .then((res) => res.data);
}

// Internal transfer counterpart candidates for a mutation
export async function getInternalTransferCandidates(
  mutationId: number,
  params?: { withinDays?: number; strictAmount?: boolean; limit?: number },
): Promise<BankMutation[]> {
  const search = new URLSearchParams();
  if (params?.withinDays !== undefined) search.set('withinDays', String(params.withinDays));
  if (params?.strictAmount !== undefined) search.set('strictAmount', String(params.strictAmount));
  if (params?.limit !== undefined) search.set('limit', String(params.limit));
  const qs = search.toString();
  return httpService
    .get<
      BankMutation[]
    >(`/business/financial/mutations/${mutationId}/internal-transfer-candidates${qs ? `?${qs}` : ''}`)
    .then((res) => res.data);
}

// Atomically create both transactions from two mutations and link them
export async function createInternalTransferFromMutations(
  fromMutationId: number,
  toMutationId: number,
  description?: string,
): Promise<{
  internalTransfer: InternalBankTransfer;
  fromTransactionId: number;
  toTransactionId: number;
}> {
  return httpService
    .post<{
      internalTransfer: InternalBankTransfer;
      fromTransactionId: number;
      toTransactionId: number;
    }>(`/business/financial/internal-transfers/from-mutations`, {
      fromMutationId,
      toMutationId,
      description,
    })
    .then((res) => res.data);
}
